import { api } from "@/lib/api/client";
import { RESERVED_SUBDOMAINS } from "@/features/onboarding/schemas";

export interface PublicContext {
  tenant: string | null;
  /** C8 : domaine racine PRIMAIRE (géré depuis la console éditeur). */
  primary_domain: string;
}

export interface OnboardingPayload {
  slug: string;
  name: string;
  admin_email: string;
  /** C12 : consentement explicite à la politique de confidentialité. */
  privacy_accepted: boolean;
}

export interface OnboardingStatus {
  status: "pending" | "running" | "succeeded" | "failed";
  steps_total: number;
  steps_done: number;
  /** Avancement réel 0-100 (avance migration par migration). */
  progress: number;
  /** Étape en cours : base, migrations, organisation, admin, finalisation, ou "" une fois terminé. */
  step: string;
}

export interface OnboardingResult {
  slug: string;
  status?: string;
}

/**
 * Domaine racine SaaS dérivé du host courant (fallback build-time — la
 * valeur de référence est le domaine PRIMAIRE servi par /public/context/,
 * cf. usePrimaryDomain). "app.pandje.tld" → "pandje.tld" ; sinon host tel quel.
 *
 * C1 : ce set est le MIROIR FRONT du minimum contractuel de
 * TENANT_RESERVED_SUBDOMAINS (backend, env) — double source assumée et
 * documentée : le back reste l'autorité (validation d'inscription,
 * résolution) ; ici il ne sert qu'à déduire le domaine racine du host.
 */
export function saasBaseDomain(): string {
  const host = window.location.hostname;
  const [first, ...rest] = host.split(".");
  if (rest.length >= 2 && first && RESERVED_SUBDOMAINS.has(first)) {
    return rest.join(".");
  }
  return host;
}

/**
 * Hôte « nu » (madagn.com, pandje.localhost) : c'est toujours le portail,
 * jamais un espace clinique (qui vit sous `<slug>.<domaine>`). La vitrine
 * s'y affiche sans attendre `/public/context/` : cette attente coûtait un
 * aller-retour réseau complet avant le premier pixel. Sur un hôte plus long,
 * on ne peut pas trancher sans le back (portail de préproduction ou espace).
 */
export function isBareHost(hostname: string): boolean {
  return hostname.split(".").length <= 2;
}

/**
 * Compat arbitrage A (2026-07-04) : l'ex-hôte console « dalan.* » redirige
 * vers « ada.* », même chemin/query/hash. Retirer après 2026-08-04.
 * Fonction pure (testable) : renvoie l'URL cible, ou null si hors périmètre.
 */
export function legacyConsoleRedirectUrl(loc: {
  hostname: string;
  protocol: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
}): string | null {
  const [first, ...rest] = loc.hostname.split(".");
  if (first !== "dalan" || rest.length < 2) {
    return null;
  }
  const p = loc.port ? `:${loc.port}` : "";
  return `${loc.protocol}//ada.${rest.join(".")}${p}${loc.pathname}${loc.search}${loc.hash}`;
}

/** Applique la redirection dalan.* → ada.* ; true si déclenchée. */
export function redirectLegacyConsoleHost(): boolean {
  const target = legacyConsoleRedirectUrl(window.location);
  if (target) {
    window.location.replace(target);
    return true;
  }
  return false;
}

/**
 * Les alias du portail (app.<domaine>, www.<domaine>) redirigent vers le
 * domaine nu — la vitrine n'a qu'une seule adresse canonique.
 * Renvoie true si une redirection a été déclenchée.
 */
export function redirectPortalAlias(): boolean {
  const { hostname, protocol, port, pathname, search } = window.location;
  const aliases = new Set(["app", "www"]);
  const [first, ...rest] = hostname.split(".");
  if (rest.length >= 2 && first && aliases.has(first)) {
    const p = port ? `:${port}` : "";
    window.location.replace(`${protocol}//${rest.join(".")}${p}${pathname}${search}`);
    return true;
  }
  return false;
}

export function tenantUrl(
  slug: string,
  domain: string = saasBaseDomain(),
  path: string = "/login",
): string {
  const port = window.location.port ? `:${window.location.port}` : "";
  return `${window.location.protocol}//${slug}.${domain}${port}${path}`;
}

export const onboardingApi = {
  context: async (): Promise<PublicContext> => {
    const { data } = await api.get<PublicContext>("/public/context/");
    return data;
  },
  signup: async (payload: OnboardingPayload): Promise<OnboardingResult> => {
    const { data } = await api.post<OnboardingResult>("/public/onboarding/", payload);
    return data;
  },
  /** Échange le ticket d'onboarding contre une session (cookies JWT posés). */
  onboardingLogin: async (ticket: string): Promise<void> => {
    await api.post("/auth/onboarding-login/", { ticket });
  },
  /** Échange un ticket de SUPPORT contre une session (accès staff à un espace). */
  supportLogin: async (ticket: string): Promise<void> => {
    await api.post("/auth/support-login/", { ticket });
  },
  status: async (slug: string): Promise<OnboardingStatus> => {
    const { data } = await api.get<OnboardingStatus>(
      `/public/onboarding/${slug}/status/`,
    );
    return data;
  },
};
